/**
 * Componente PlansGrid
 * Muestra la cuadrícula de planes disponibles
 */

import React from 'react';
import { FiRefreshCw, FiSettings } from 'react-icons/fi';
import Button from '../UI/Button';
import PlanCard from '../subscription/PlanCard';

/**
 * Componente PlansGrid
 * @param {Object} props - Propiedades del componente
 * @param {Array} props.plans - Lista de planes disponibles
 * @param {Object} props.subscription - Suscripción actual del usuario
 * @param {boolean} props.loading - Estado de carga
 * @param {boolean} props.error - Estado de error
 * @param {Function} props.onRefresh - Función para refrescar datos
 * @param {Function} props.onSubscribe - Función para suscribirse
 * @param {Function} props.onChangePlan - Función para cambiar de plan
 */
const PlansGrid = ({ 
  plans, 
  subscription, 
  loading, 
  error, 
  onRefresh, 
  onSubscribe, 
  onChangePlan 
}) => {
  // Estado de carga
  if (loading) {
    return (
      <div className="flex justify-center items-center py-20">
        <FiRefreshCw className="animate-spin h-8 w-8 text-primary" />
        <span className="ml-3 text-gray-600 dark:text-gray-400">
          Cargando planes...
        </span>
      </div>
    );
  }

  // Estado de error
  if (error) {
    return (
      <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-6 text-center">
        <p className="text-red-800 dark:text-red-200 mb-4">{error}</p>
        <Button 
          variant="outline" 
          onClick={onRefresh}
        >
          Reintentar
        </Button>
      </div>
    );
  }

  // Sin planes disponibles
  if (plans.length === 0) {
    return (
      <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-sm p-8 text-center">
        <FiSettings className="h-12 w-12 text-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
          No hay planes disponibles
        </h3>
        <p className="text-gray-600 dark:text-gray-300">
          En este momento no hay planes disponibles. Por favor, intenta más tarde.
        </p>
      </div>
    );
  }

  // Lista de planes
  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
          Planes Disponibles
        </h2>
        <Button
          variant="ghost"
          size="sm"
          onClick={onRefresh}
          loading={loading}
        >
          <FiRefreshCw className="h-4 w-4" />
        </Button>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {plans.map((plan) => {
          // Determinar si el usuario ya tiene este plan
          const isCurrentPlan = subscription?.plan_id === plan.id;
          
          return (
            <PlanCard
              key={plan.id}
              plan={plan}
              userSubscription={subscription}
              onSubscribe={isCurrentPlan ? onChangePlan : onSubscribe}
              loading={loading}
              popular={plan.popular || false}
              recommended={plan.recomendado || false}
              showCurrentPlanBadge={isCurrentPlan}
            />
          );
        })}
      </div>
    </div>
  );
};

export default PlansGrid;
